import { useMemo, useState, useRef, useEffect } from "react";
import PageHeader from "./PageHeader";
import { useSiteContent } from "../../context/SiteContext";
import { useAudio } from "../../context/AudioContext";
import { Play, Pause, Search, ShoppingCart, ArrowUpRight } from "lucide-react";

function PurchaseButton({ beat, storefrontUrl }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const links = beat.purchaseLinks || {};
  const platforms = Object.entries(links).filter(([, url]) => url);
  const hasPlatforms = platforms.length > 0;

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!hasPlatforms && !storefrontUrl) return null;

  if (!hasPlatforms && storefrontUrl) {
    return (
      <a
        href={storefrontUrl}
        target="_blank"
        rel="noreferrer"
        className="border border-[#27272A] hover:border-neon hover:text-neon p-2 transition-colors inline-flex"
        title="Purchase"
      >
        <ShoppingCart size={14} />
      </a>
    );
  }

  if (platforms.length === 1) {
    return (
      <a
        href={platforms[0][1]}
        target="_blank"
        rel="noreferrer"
        className="border border-[#27272A] hover:border-neon hover:text-neon p-2 transition-colors inline-flex"
        title={`Buy on ${platforms[0][0]}`}
      >
        <ShoppingCart size={14} />
      </a>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`border p-2 transition-colors inline-flex ${open ? "border-neon text-neon" : "border-[#27272A] hover:border-neon hover:text-neon"}`}
        title="Purchase options"
      >
        <ShoppingCart size={14} />
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-2 bg-[#0A0A0A] border border-[#27272A] z-20 min-w-[180px]">
          <div className="label-mono text-neutral-500 px-3 py-2 border-b border-[#27272A]">// purchase</div>
          {platforms.map(([platform, url]) => (
            <a
              key={platform}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between px-3 py-2 text-sm font-mono hover:bg-[#111111] hover:text-neon transition-colors"
            >
              {platform.replace(/_/g, " ")}
              <ArrowUpRight size={12} />
            </a>
          ))}
          {storefrontUrl && (
            <a
              href={storefrontUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between px-3 py-2 text-sm font-mono border-t border-[#27272A] text-neutral-400 hover:bg-[#111111] hover:text-neon transition-colors"
            >
              Full catalog
              <ArrowUpRight size={12} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default function Beatbox() {
  const content = useSiteContent();
  const audio = useAudio();
  const [q, setQ] = useState("");
  const [mood, setMood] = useState("ALL");
  const beats = content.beats;
  const storefront = content.storefront || {};

  const moods = useMemo(() => {
    const set = new Set(beats.map((b) => b.mood));
    return ["ALL", ...Array.from(set)];
  }, [beats]);

  const filtered = useMemo(() => {
    return beats.filter((b) => {
      const inMood = mood === "ALL" || b.mood === mood;
      const ql = q.trim().toLowerCase();
      const inQ =
        !ql ||
        b.name.toLowerCase().includes(ql) ||
        b.key.toLowerCase().includes(ql) ||
        b.tags.some((t) => t.toLowerCase().includes(ql)) ||
        String(b.bpm).includes(ql);
      return inMood && inQ;
    });
  }, [q, mood, beats]);

  return (
    <div data-testid="page-beatbox">
      <PageHeader
        code="03"
        label="BEATBOX"
        title="Beatbox"
        subtitle="The instrumental archive. Every beat indexed, tagged, and ready to license. Filter the grid, find your tempo."
      />

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          {/* Storefront Banner */}
          {storefront.url && (
            <a
              href={storefront.url}
              target="_blank"
              rel="noreferrer"
              data-testid="storefront-banner"
              className="em-card p-5 mb-6 flex items-center justify-between group block"
            >
              <div>
                <div className="label-mono text-neon">// STOREFRONT</div>
                <div className="font-display font-bold text-lg mt-1 group-hover:text-neon transition-colors">
                  {storefront.title || "Browse the full catalog"}
                </div>
                {storefront.description && (
                  <div className="text-neutral-400 text-sm mt-1">{storefront.description}</div>
                )}
              </div>
              <ArrowUpRight size={24} className="text-neutral-500 group-hover:text-neon transition-colors flex-shrink-0" />
            </a>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex items-center gap-2 border border-[#27272A] px-3 py-2 flex-1">
              <Search size={16} className="text-neutral-500" />
              <input
                data-testid="beats-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="search name / key / tag / bpm"
                className="bg-transparent outline-none w-full font-mono text-sm placeholder:text-neutral-600"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {moods.map((m) => (
                <button
                  key={m}
                  data-testid={`beats-mood-${m.toLowerCase()}`}
                  onClick={() => setMood(m)}
                  className={`label-mono px-3 py-2 border transition-colors ${
                    mood === m
                      ? "border-neon text-neon"
                      : "border-[#27272A] text-neutral-400 hover:text-neon hover:border-neon/60"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="em-card overflow-x-auto" data-testid="beats-table">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
                <tr className="label-mono text-neutral-500">
                  <th className="text-left p-4">ID</th>
                  <th className="text-left p-4">Name</th>
                  <th className="text-left p-4">BPM</th>
                  <th className="text-left p-4">Key</th>
                  <th className="text-left p-4">Mood</th>
                  <th className="text-left p-4 hidden md:table-cell">Tags</th>
                  <th className="text-right p-4">Price</th>
                  <th className="text-right p-4">·</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`beat-row-${b.id}`}
                    className="border-b border-[#1A1A1A] hover:bg-[#0F0F0F] transition-colors group"
                  >
                    <td className="p-4 font-mono text-neutral-500">{b.id}</td>
                    <td className="p-4 font-display font-bold group-hover:text-neon">{b.name}</td>
                    <td className="p-4 font-mono text-neon">{b.bpm}</td>
                    <td className="p-4 font-mono">{b.key}</td>
                    <td className="p-4">
                      <span className="label-mono border border-[#27272A] px-2 py-1">
                        {b.mood}
                      </span>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {b.tags.map((t) => (
                          <span
                            key={t}
                            className="label-mono text-neutral-500 border border-[#27272A] px-2 py-0.5"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-4 text-right font-mono text-neon">${b.price}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PurchaseButton beat={b} storefrontUrl={storefront.url} />
                        {(() => {
                          const isCurrent = b.audioUrl && audio.src === b.audioUrl;
                          const isPlaying = isCurrent && audio.playing;
                          return (
                            <button
                              data-testid={`beat-play-${b.id}`}
                              onClick={() => {
                                if (!b.audioUrl) return;
                                isCurrent ? audio.toggle() : audio.play(b.audioUrl);
                              }}
                              disabled={!b.audioUrl}
                              className={`border p-2 transition-colors ${
                                b.audioUrl
                                  ? isPlaying
                                    ? "border-neon text-neon"
                                    : "border-[#27272A] hover:border-neon hover:text-neon"
                                  : "border-[#1A1A1A] text-neutral-700 cursor-not-allowed"
                              }`}
                              aria-label={b.audioUrl ? `${isPlaying ? "Pause" : "Preview"} ${b.name}` : "No preview available"}
                              title={b.audioUrl ? "Preview" : "No preview available"}
                            >
                              {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-12 text-center text-neutral-500 label-mono">
                      // no matches — adjust filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-neutral-500 text-xs mt-6 label-mono">
            // {filtered.length} beat{filtered.length !== 1 ? "s" : ""} indexed
          </p>
        </div>
      </section>
    </div>
  );
}
